import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';

import type { GameAssets } from '../../game/assets/GameAssets';
import { WORLD_TILE_HEIGHT, WORLD_TILE_WIDTH } from '../../game/config';
import type { SeasonState } from '../seasons/SeasonTypes';
import { castleStageKey, seasonalBuildingKey } from './DynamicWorldRules';
import type { WorldEntity } from '../world/types';

interface EntityView {
  readonly root: Container;
  readonly labelRoot: Container;
  readonly ground: Sprite;
  readonly sprite: Sprite;
  readonly appearance: Graphics;
  readonly seasonal: Sprite;
  readonly label: Text;
  readonly status: Text;
}

interface EntityRendererOptions {
  readonly visualPolish?: boolean;
  readonly showAllLabels?: boolean;
}

export class EntityRenderer {
  private readonly views = new Map<string, EntityView>();
  private readonly labelLayer = new Container();
  private season?: SeasonState;

  constructor(
    private readonly layer: Container,
    private readonly assets: GameAssets,
    private readonly options: EntityRendererOptions = {},
  ) {
    this.layer.sortableChildren = true;
    this.labelLayer.label = 'Entity Nameplates';
    this.labelLayer.sortableChildren = true;
    this.labelLayer.zIndex = 1_000_000;
    this.layer.addChild(this.labelLayer);
  }

  render(entities: readonly WorldEntity[], season?: SeasonState): void {
    this.season = season;
    const seen = new Set<string>();

    for (const entity of entities) {
      seen.add(entity.id);
      const view = this.views.get(entity.id) ?? this.createView(entity);
      this.drawEntity(view, entity);
    }

    for (const [id, view] of this.views) {
      if (!seen.has(id)) {
        view.root.destroy({ children: true });
        view.labelRoot.destroy({ children: true });
        this.views.delete(id);
      }
    }
  }

  private createView(entity: WorldEntity): EntityView {
    const root = new Container();
    const labelRoot = new Container();
    const ground = new Sprite(Texture.EMPTY);
    const sprite = new Sprite(Texture.EMPTY);
    const appearance = new Graphics();
    const seasonal = new Sprite(Texture.EMPTY);
    const label = new Text({
      text: entity.label ?? entity.kind,
      style: {
        fill: entity.type === 'unit' ? '#ffe8a3' : '#f6d47a',
        fontFamily: 'Georgia, serif',
        fontSize: this.options.visualPolish ? (entity.type === 'unit' ? 13 : 17) : entity.type === 'unit' ? 14 : 15,
        fontWeight: 'bold',
        stroke: { color: '#1d1204', width: this.options.visualPolish ? 6 : 5 },
      },
    });
    const status = new Text({
      text: '',
      style: {
        fill: '#f7d889',
        fontFamily: 'Georgia, serif',
        fontSize: this.options.visualPolish ? 13 : 12,
        fontWeight: 'bold',
        stroke: { color: '#1d1204', width: 4 },
      },
    });

    ground.anchor.set(0.5, 1);
    sprite.anchor.set(0.5, 1);
    seasonal.anchor.set(0.5, 1);
    ground.roundPixels = true;
    sprite.roundPixels = true;
    seasonal.roundPixels = true;
    label.anchor.set(0.5, 1);
    status.anchor.set(0.5, 1);
    root.addChild(ground, sprite, appearance, seasonal);
    labelRoot.addChild(label, status);
    this.layer.addChild(root);
    this.labelLayer.addChild(labelRoot);

    const view = { root, labelRoot, ground, sprite, appearance, seasonal, label, status };
    this.views.set(entity.id, view);
    return view;
  }

  private drawEntity(view: EntityView, entity: WorldEntity): void {
    view.root.visible = !entity.hidden;
    view.labelRoot.visible = !entity.hidden;
    if (entity.hidden) return;

    view.sprite.texture = this.resolveTexture(entity);
	this.drawAppearance(view.appearance, entity);
	view.sprite.visible = !entity.appearance;
    view.ground.texture = this.resolveGroundLayer(entity);
    view.seasonal.texture = this.resolveSeasonLayer(entity);

    const renderX = entity.x + (entity.type === 'unit' ? entity.roadOffsetX ?? 0 : 0);
    const renderY = entity.y + (entity.type === 'unit' ? entity.roadOffsetY ?? 0 : 0);
    const tileCenterX = renderX * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2;
    const tileFootY = entity.y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT;
    const tileCenterY = renderY * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT / 2;
    const centeredEnvironment = entity.type === 'environment' && entity.kind.startsWith('bridge');
    const entityY = centeredEnvironment || entity.type === 'unit' ? tileCenterY : tileFootY;
    view.root.position.set(tileCenterX, entityY);
    view.labelRoot.position.set(tileCenterX, entityY);
    view.labelRoot.zIndex = Math.floor(entityY);
    // A bridge is part of the walkable ground. Keep its complete sprite below
    // every gameplay actor so the deck and railings cannot hide a character
    // while crossing. It still renders above the terrain because entities live
    // on their own world layer.
    view.root.zIndex = Math.floor(
      centeredEnvironment ? entityY - 1_000 : entityY + (entity.type === 'building' ? 18 : 0),
    );

    const scale = this.resolveScale(entity);
    view.sprite.anchor.set(0.5, centeredEnvironment ? 0.5 : 1);
    view.ground.anchor.set(0.5, centeredEnvironment ? 0.5 : 1);
    view.seasonal.anchor.set(0.5, centeredEnvironment ? 0.5 : 1);
    const unitFlip = entity.type === 'unit' && entity.facing === 'west' ? -1 : 1;
    const wallFlip = entity.type === 'building'
      && entity.kind.startsWith('wall_')
      && entity.facing === 'west'
      ? -1
      : 1;
    const unitWalkBob = entity.type === 'unit' && entity.activity !== 'idle' ? -Math.round(Math.abs(Math.cos(entity.animationPhase ?? 0)) * 1.5) : 0;
    // The main crossing now uses a native wide, high-resolution texture. Keep
    // it close to authored size instead of stretching a 64px tile by 338%.
    const isMainBridge = entity.id === 'bridge-main-composite-crossing' && this.options.visualPolish;
    const bridgeStretch = isMainBridge ? 0.75 : 1;
    const bridgeHeightScale = isMainBridge ? 0.76 : scale;
    view.sprite.scale.set(scale * bridgeStretch * unitFlip * wallFlip, bridgeHeightScale);
    view.sprite.y = unitWalkBob;
	if (entity.appearance) {
		const appearanceScale = this.options.visualPolish ? 0.62 : 0.66;
		view.appearance.scale.set(appearanceScale * unitFlip, appearanceScale);
		view.appearance.y = 2 + unitWalkBob;
		view.appearance.x = 0;
	}
    view.ground.visible = entity.type === 'building' && !this.options.visualPolish;
    view.ground.alpha = 0.46;
    view.ground.width = this.resolveGroundWidth(entity);
    view.ground.height = this.resolveGroundHeight(entity);
    view.ground.y = 5;

    view.seasonal.visible = entity.type === 'building' && view.seasonal.texture !== Texture.EMPTY;
    view.seasonal.scale.set(scale * 1.02);
    view.seasonal.y = -Math.max(28, view.sprite.height * 0.18);

    view.label.text = this.resolveLabel(entity);
    view.label.visible = this.shouldShowLabel(entity);
	view.label.y = entity.appearance
		? (entity.appearance.isStreamer ? -64 : -53)
		: -Math.max(entity.type === 'unit' ? 22 : 36, view.sprite.height + (entity.type === 'unit' ? 0 : 4));
    view.status.text = this.resolveStatus(entity);
    view.status.visible = !this.options.visualPolish && entity.type === 'unit' && Boolean(view.status.text);
    view.status.y = view.label.visible ? view.label.y + 14 : -22;
  }

  private drawAppearance(graphics: Graphics, entity: WorldEntity): void {
    graphics.clear();
    if (entity.type !== 'unit' || !entity.appearance) {
      graphics.visible = false;
      return;
    }

    const colors = entity.appearance;
    this.drawVoxelAppearance(graphics, entity);
    return;

    const moving = entity.activity !== undefined && entity.activity !== 'idle' && entity.activity !== 'enter';
    const facingAway = entity.facing === 'north';
    const stride = moving ? Math.sin(entity.animationPhase ?? 0) * 4 : 0;
    const leftLift = Math.max(0, stride);
    const rightLift = Math.max(0, -stride);
    const armSwing = moving ? Math.sin(entity.animationPhase ?? 0) * 3 : 0;
    graphics.visible = true;
	graphics.ellipse(0, -2, 28, 7).fill({ color: '#050505', alpha: 0.34 });
	if (colors.gender === 'female') {
		graphics.rect(-12, -63, 6, 30).fill(colors.hairColor);
		graphics.rect(7, -63, 5, 30).fill(colors.hairColor);
	}

	// Boots and separated trouser legs.
	graphics.rect(-11 - stride * 0.35, -9 - leftLift, 10, 9).fill('#160f0a');
	graphics.rect(1 + stride * 0.35, -9 - rightLift, 10, 9).fill('#160f0a');
	graphics.rect(-9 - stride * 0.22, -19 - leftLift * 0.45, 8, 12).fill(colors.pantsColor);
	graphics.rect(1 + stride * 0.22, -19 - rightLift * 0.45, 8, 12).fill(colors.pantsColor);
	graphics.rect(-8 - stride * 0.22, -18 - leftLift * 0.45, 2, 10).fill({ color: '#ffffff', alpha: 0.18 });
	graphics.rect(-11, -23, 22, 5).fill('#3b291b');
	graphics.rect(-2, -22, 4, 3).fill('#d6a84f');

	// Torso, sleeves and hands.
	graphics.rect(-14, -42, 28, 21).fill('#160f0a');
	graphics.rect(-12, -40, 24, 17).fill(colors.shirtColor);
	graphics.rect(-11, -39, 3, 14).fill({ color: '#ffffff', alpha: 0.2 });
	graphics.rect(-18, -39 + armSwing, 6, 15).fill('#160f0a');
	graphics.rect(12, -39 - armSwing, 6, 15).fill('#160f0a');
	graphics.rect(-16, -37 + armSwing, 4, 9).fill(colors.shirtColor);
	graphics.rect(12, -37 - armSwing, 4, 9).fill(colors.shirtColor);
	graphics.rect(-16, -28 + armSwing, 4, 6).fill('#f0b985');
	graphics.rect(12, -28 - armSwing, 4, 6).fill('#f0b985');
	if (facingAway) {
		graphics.rect(-2, -39, 4, 15).fill({ color: '#160f0a', alpha: 0.28 });
	} else {
		graphics.rect(-2, -34, 4, 5).fill('#f2c14e');
	}
	if (colors.gender === 'female') {
		graphics.poly([-11, -25, 11, -25, 14, -17, -14, -17]).fill(colors.shirtColor);
		graphics.rect(-10, -23, 3, 5).fill({ color: '#ffffff', alpha: 0.18 });
	}

	// Neck and head. North-facing units show a real rear view instead of
	// walking backwards with their face still visible.
	graphics.rect(-4, -47, 8, 7).fill('#f0b985');
	graphics.rect(-11, -63, 22, 18).fill('#160f0a');
	if (facingAway) {
		graphics.rect(-9, -61, 18, 15).fill(colors.hairColor);
		graphics.rect(-11, -66, 22, 10).fill(colors.hairColor);
		graphics.rect(-11, -58, 5, 10).fill(colors.hairColor);
		graphics.rect(7, -58, 4, 9).fill(colors.hairColor);
		graphics.rect(-7, -63, 12, 2).fill({ color: '#ffffff', alpha: 0.16 });
		graphics.rect(-8, -49, 16, 3).fill({ color: '#160f0a', alpha: 0.24 });
	} else {
		graphics.rect(-9, -61, 18, 15).fill('#f0c28c');
		graphics.rect(-11, -66, 22, 8).fill(colors.hairColor);
		graphics.rect(-11, -60, 5, 9).fill(colors.hairColor);
		graphics.rect(7, -60, 4, 7).fill(colors.hairColor);
		graphics.rect(-8, -64, 7, 2).fill({ color: '#ffffff', alpha: 0.18 });
		graphics.rect(-5, -55, 2, 2).fill('#2a190f');
		graphics.rect(4, -55, 2, 2).fill('#2a190f');
		graphics.rect(-1, -50, 4, 2).fill('#b56f54');
	}
	if (colors.isStreamer) {
		graphics.poly([-13, -67, -13, -78, -7, -72, -3, -84, 2, -73, 8, -85, 11, -73, 15, -79, 13, -67]).fill('#f2c14e');
		graphics.rect(-13, -70, 26, 5).fill('#d99018');
		graphics.rect(-9, -69, 4, 2).fill('#fff1a8');
		graphics.rect(0, -69, 4, 2).fill('#fff1a8');
		graphics.rect(8, -69, 3, 2).fill('#fff1a8');
	}
  }

  private drawVoxelAppearance(graphics: Graphics, entity: WorldEntity): void {
    if (!entity.appearance) return;
    const colors = entity.appearance;
    const skin = '#f3c79a';
    const boot = '#3d2a1c';
    const profession = this.professionColor(entity.kind);
    const walking = entity.activity === 'walk' || entity.activity === 'carry';
    const working = entity.activity === 'work';
    const stride = walking ? Math.sin(entity.animationPhase ?? 0) * 2.4 : 0;
    const workStroke = working ? Math.sin(entity.animationPhase ?? 0) * 3.2 : 0;
    const facingAway = entity.facing === 'north';
    const facingSide = entity.facing === 'east' || entity.facing === 'west';
    const female = colors.gender === 'female';
    const leftLift = walking ? Math.max(0, stride) * 0.75 : 0;
    const rightLift = walking ? Math.max(0, -stride) * 0.75 : 0;

    graphics.visible = true;
    graphics.ellipse(0, 2, 29, 7).fill({ color: 0x050505, alpha: 0.28 });

    // Slim, articulated cuboids mirror the proportions of a polished voxel
    // character instead of assembling the body from equally sized blocks.
    this.drawVoxelCuboid(graphics, -8 + stride, -21 - leftLift, 6, 17, 2, colors.pantsColor);
    this.drawVoxelCuboid(graphics, -9 + stride, -7 - leftLift, 8, 7, 2, boot);
    this.drawVoxelCuboid(graphics, 2 - stride, -21 - rightLift, 6, 17, 2, colors.pantsColor);
    this.drawVoxelCuboid(graphics, 1 - stride, -7 - rightLift, 8, 7, 2, boot);

    const torsoWidth = facingSide ? (female ? 15 : 17) : (female ? 20 : 23);
    const torsoX = -torsoWidth / 2;
    this.drawVoxelCuboid(graphics, torsoX, -49, torsoWidth, 29, 3, colors.shirtColor);
    if (female) {
      this.drawVoxelCuboid(graphics, -12, -27, 24, 9, 3, colors.shirtColor);
    }
    this.drawVoxelCuboid(graphics, torsoX, -24, torsoWidth, 4, 3, profession);

    const armSwing = walking ? stride * 1.4 : workStroke;
    this.drawVoxelCuboid(graphics, torsoX - 7, -47 + armSwing, 6, 14, 2, colors.shirtColor);
    this.drawVoxelCuboid(graphics, torsoX - 7, -34 + armSwing, 6, 11, 2, skin);
    this.drawVoxelCuboid(graphics, torsoX + torsoWidth + 1, -47 - armSwing, 6, 14, 2, colors.shirtColor);
    this.drawVoxelCuboid(graphics, torsoX + torsoWidth + 1, -34 - armSwing, 6, 11, 2, skin);

    this.drawVoxelCuboid(graphics, -4, -55, 8, 7, 2, skin);
    const headX = facingSide ? -11 : -14;
    const headWidth = facingSide ? 22 : 28;
    this.drawVoxelCuboid(graphics, headX, -76, headWidth, 23, 4, facingAway ? colors.hairColor : skin);
    this.drawVoxelCuboid(graphics, headX - 1, -80, headWidth + 2, 8, 4, colors.hairColor);
    this.drawVoxelCuboid(graphics, headX - 1, -74, 5, 17, 3, colors.hairColor);
    this.drawVoxelCuboid(graphics, headX + headWidth - 4, -74, 5, female ? 20 : 13, 3, colors.hairColor);

    if (facingSide) {
      // Side profile is authored facing east; the root is mirrored for west.
      // A single eye, forward nose and narrower torso make direction readable
      // without replacing the player's selected colours or profession gear.
      graphics.rect(5, -68, 4, 4).fill('#2d241f');
      graphics.rect(6, -69, 2, 1).fill('#fff4db');
      this.drawVoxelCuboid(graphics, 9, -65, 5, 5, 2, skin);
      graphics.rect(5, -59, 5, 2).fill('#b56f54');
    } else if (!facingAway) {
      graphics.rect(-8, -68, 4, 4).fill('#2d241f');
      graphics.rect(5, -68, 4, 4).fill('#2d241f');
      graphics.rect(-7, -69, 2, 1).fill('#fff4db');
      graphics.rect(6, -69, 2, 1).fill('#fff4db');
      graphics.rect(-2, -59, 5, 2).fill('#b56f54');
    }

    if (female) {
      this.drawVoxelCuboid(graphics, -19, -72, 6, 12, 3, colors.hairColor);
      this.drawVoxelCuboid(graphics, 14, -72, 6, 12, 3, colors.hairColor);
    }

    if (entity.kind === 'knight') {
      this.drawVoxelCuboid(graphics, -16, -82, 32, 8, 4, profession);
      this.drawVoxelCuboid(graphics, -18, -49, 7, 8, 3, profession);
      this.drawVoxelCuboid(graphics, 11, -49, 7, 8, 3, profession);
    } else if (entity.kind === 'farmer') {
      this.drawVoxelCuboid(graphics, -19, -82, 38, 5, 3, '#d7ae53');
    } else if (entity.kind === 'miner') {
      this.drawVoxelCuboid(graphics, -16, -82, 32, 7, 4, '#a9b3b9');
      this.drawVoxelCuboid(graphics, -3, -85, 6, 5, 2, '#f5d35b');
    } else if (entity.kind === 'woodcutter' || working) {
      const toolY = -39 - armSwing;
      this.drawVoxelCuboid(graphics, 18, toolY, 4, 28, 2, '#76502d');
      this.drawVoxelCuboid(graphics, 14, toolY - 4, 13, 6, 2, '#aeb7c1');
    } else if (entity.kind === 'trader') {
      this.drawVoxelCuboid(graphics, 13, -40, 9, 18, 3, profession);
      graphics.rect(-10, -47, 3, 26).fill({ color: this.shadeColor(profession, -0.28), alpha: 0.9 });
    }

    if (colors.isStreamer) {
      this.drawVoxelCuboid(graphics, -15, -86, 30, 6, 3, '#d99018');
      this.drawVoxelCuboid(graphics, -13, -94, 6, 9, 2, '#ffd85a');
      this.drawVoxelCuboid(graphics, -3, -97, 6, 12, 2, '#ffd85a');
      this.drawVoxelCuboid(graphics, 7, -94, 6, 9, 2, '#ffd85a');
    }
  }

  private drawVoxelCuboid(
    graphics: Graphics,
    x: number,
    y: number,
    width: number,
    height: number,
    depth: number,
    color: string,
  ): void {
    const front = this.shadeColor(color, 0.02);
    const top = this.shadeColor(color, 0.3);
    const side = this.shadeColor(color, -0.24);
    const outline = { color: 0x2b1c14, width: 0.7, alpha: 0.72 };

    graphics.rect(x, y, width, height).fill(front).stroke(outline);
    graphics
      .poly([x, y, x + depth, y - depth, x + width + depth, y - depth, x + width, y])
      .fill(top)
      .stroke(outline);
    graphics
      .poly([x + width, y, x + width + depth, y - depth, x + width + depth, y + height - depth, x + width, y + height])
      .fill(side)
      .stroke(outline);
  }

  private shadeColor(value: string, factor: number): number {
    const normalized = /^#[0-9a-f]{6}$/i.test(value) ? value.slice(1) : '777777';
    const channels = [0, 2, 4].map((index) => Number.parseInt(normalized.slice(index, index + 2), 16));
    const adjusted = channels.map((channel) => {
      const next = factor >= 0
        ? channel + (255 - channel) * factor
        : channel * (1 + factor);
      return Math.max(0, Math.min(255, Math.round(next)));
    });
    return (adjusted[0] << 16) | (adjusted[1] << 8) | adjusted[2];
  }

  private professionColor(kind: string): string {
    if (kind === 'knight') return '#aeb7c1';
    if (kind === 'woodcutter') return '#8b5a2b';
    if (kind === 'trader') return '#d0a83b';
    return '#b9914a';
  }

  private shouldShowLabel(entity: WorldEntity): boolean {
    if (entity.type === 'environment') return false;
    if (this.options.showAllLabels) return true;
    if (!this.options.visualPolish) return true;
    if (entity.type === 'unit') return Boolean(entity.label);
    return ['castle', 'warehouse', 'market', 'watchtower', 'farm'].includes(entity.kind);
  }

  private resolveLabel(entity: WorldEntity): string {
    if (entity.type !== 'building' || !entity.building) return entity.label ?? entity.kind;
    return entity.label ?? entity.kind;
  }

  private resolveStatus(entity: WorldEntity): string {
    if (entity.type !== 'unit') return '';
    if (entity.activity === 'carry') return 'trägt';
    if (entity.activity === 'work') return 'arbeitet';
    if (entity.activity === 'walk') return 'läuft';
    return '';
  }

  private resolveTexture(entity: WorldEntity): Texture {
    if (entity.type === 'building') return this.resolveBuildingTexture(entity);
    if (entity.type === 'unit') return this.assets.units[entity.kind] ?? this.assets.units.worker;
    return this.resolveEnvironmentTexture(entity.kind);
  }

  private resolveGroundLayer(entity: WorldEntity): Texture {
    if (entity.type !== 'building') return Texture.EMPTY;
    const season = this.season?.currentSeason ?? 'summer';
    return this.assets.reference[`building_layers/${season}_ground`] ?? Texture.EMPTY;
  }

  private resolveSeasonLayer(entity: WorldEntity): Texture {
    if (entity.type !== 'building') return Texture.EMPTY;
    const season = this.season?.currentSeason;
    if (season !== 'winter' && season !== 'autumn') return Texture.EMPTY;
    return this.assets.reference[`building_layers/${season}_overlay`] ?? Texture.EMPTY;
  }

  private resolveScale(entity: WorldEntity): number {
    if (entity.type === 'building') {
      if (entity.kind.startsWith('wall_')) return this.options.visualPolish ? 0.54 : 0.46;
      const polishBoost = this.options.visualPolish ? 1.15 : 1;
      const stageBoost = entity.building ? 0.9 + entity.building.stage * 0.045 : 1;
      if (entity.kind === 'castle') {
        // Castle source canvases become larger with every stage. Matching
        // authored scale factors keep the visible silhouette growing without
        // cropping the roof or making an upgrade look like a second castle.
        const castleScaleByStage = [1.5, 1.35, 1.32, 1.3, 1.18, 1.33] as const;
        const stage = Math.max(1, Math.min(6, entity.building?.stage ?? 1));
        return castleScaleByStage[stage - 1] * polishBoost;
      }
      if (entity.kind === 'mill' || entity.kind === 'watchtower') return 1.52 * polishBoost * stageBoost;
      // Markt und Lager verwenden hochauflösende 128px-Originale. Ihre
      // sichtbare Silhouette entspricht bei diesem Faktor den übrigen
      // Dorfgebäuden, ohne die großen transparenten Leinwände aufzublähen.
      if (entity.kind === 'market' || entity.kind === 'warehouse') {
        return 1.12 * polishBoost * stageBoost;
      }
      return 1.58 * polishBoost * stageBoost;
    }

    if (entity.type === 'unit') return this.options.visualPolish ? 0.48 : 0.52;
    if (entity.kind === 'lake_composite_small') return 2.35;
    if (entity.kind.startsWith('bridge')) return this.options.visualPolish ? 1.34 : 0.82;
    if (entity.kind === 'crate' || entity.kind === 'barrel' || entity.kind === 'stump') return 0.72;
    if (entity.kind === 'fence_h') return 0.86;
    if (entity.kind === 'tree_oak' || entity.kind === 'tree_pine') return this.options.visualPolish ? 1.48 : 1.38;
    if (entity.kind === 'rocks') return this.options.visualPolish ? 1.38 : 1.3;
    return 1.15;
  }

  private resolveGroundWidth(entity: WorldEntity): number {
    const width = entity.building?.footprint.width ?? 2;
    return WORLD_TILE_WIDTH * (width * 0.78 + 0.18);
  }

  private resolveGroundHeight(entity: WorldEntity): number {
    const height = entity.building?.footprint.height ?? 2;
    return WORLD_TILE_HEIGHT * (height * 0.4 + 0.18);
  }

  private resolveBuildingTexture(entity: WorldEntity): Texture {
    const kind = entity.kind;
    const season = this.season?.currentSeason;
    if (kind === 'castle') {
      const stage = entity.building?.stage ?? 1;
      const stageKey = castleStageKey(stage, season);
      return this.assets.reference[stageKey]
        ?? this.assets.reference[castleStageKey(stage)]
        ?? this.assets.buildings.castle;
    }
    const mapping: Record<string, string> = {
      warehouse: 'buildingref/warehouse',
      market: 'buildingref/market',
      house: 'buildingref/house',
      farm: 'buildingref/farm',
      woodcutter_camp: 'buildingref/woodcutter_camp',
      woodcamp: 'buildingref/woodcutter_camp',
      mine: 'buildingref/mine',
      quarry: 'buildingref/quarry',
      iron_mine: 'buildingref/iron_mine',
      clay_pit: 'buildingref/clay_pit',
      smelter: 'buildingref/smelter',
      mill: 'buildingref/mill',
      bakery: 'buildingref/bakery',
      smithy: 'buildingref/smithy',
      barracks: 'buildingref/barracks',
      watchtower: 'buildingref/watchtower',
      spy_office: 'buildingref/spy_office',
      brickyard: 'buildingref/brickyard',
      builder_yard: 'buildingref/builder_yard',
      wall: 'buildingref/wall',
      wall_straight_nw: 'buildingref/wall_straight_nw',
      wall_straight_ne: 'buildingref/wall_straight_ne',
      wall_straight_vertical: 'buildingref/wall_straight_vertical',
      wall_straight_horizontal: 'buildingref/wall_straight_horizontal',
      wall_corner: 'buildingref/wall_corner',
      wall_tower: 'buildingref/wall_tower',
      wall_gate: 'buildingref/wall_gate',
      siege_workshop: 'buildingref/siege_workshop',
      mage_tower: 'buildingref/mage_tower',
      stable: 'buildingref/stable',
      harbor: 'buildingref/harbor',
      lighthouse: 'buildingref/lighthouse',
    };
    const baseKey = mapping[kind] ?? 'buildingref/house';
    const paddedKey = `${baseKey}_padded`;
    const referenceKey = this.options.visualPolish && this.assets.reference[paddedKey] ? paddedKey : baseKey;
    const winterKey = seasonalBuildingKey(referenceKey, season);
    return this.assets.reference[winterKey]
      ?? this.assets.reference[referenceKey]
      ?? this.assets.buildings[kind]
      ?? this.assets.buildings.house;
  }

  private resolveEnvironmentTexture(kind: string): Texture {
    if (
      this.options.visualPolish &&
      (kind === 'flowers_white' ||
        kind === 'flowers_blue' ||
        kind === 'mushrooms' ||
        kind === 'log' ||
        kind === 'stump' ||
        kind === 'fence_h' ||
        kind === 'fence_corner' ||
        kind === 'banner_blue')
    ) {
      return Texture.EMPTY;
    }

    const mapping: Record<string, string> = {
      tree_oak: this.options.visualPolish ? 'props/tree_oak_clean' : 'props/tree_oak',
      tree_pine: this.options.visualPolish ? 'props/tree_pine_clean' : 'props/tree_pine',
      bridge_h: this.options.visualPolish ? 'sk/bridge_h_sharp_v2' : 'sk/bridge_h_clean_cutout',
      bridge_v: 'sk/bridge_v_clean_cutout',
      rocks: 'props/rocks',
      bush: 'props/bush',
      flowers_white: 'decor/flowers_white',
      flowers_blue: 'decor/flowers_blue',
      mushrooms: 'decor/mushrooms',
      log: 'decor/log',
      stump: 'decor/stump',
      crate: 'props/crate',
      barrel: 'props/barrel',
      fence_h: 'props/fence_h',
      fence_corner: 'decor/fence_corner',
      banner_blue: 'props/banner_blue',
      lake_composite_small: 'sk/lake_example_small',
    };
    const referenceKey = mapping[kind] ?? 'props/tree_oak';
    return this.assets.reference[referenceKey] ?? this.assets.environment[kind] ?? this.assets.environment.tree_oak;
  }
}
